import React, { useEffect, useMemo, useState } from 'react';
import type { StoryModel } from './storyModel';
import type { DraftMode, DraftRequest, DraftResponse } from '../cloud/PresentationApiClient';
import { PresenterApiError } from '../cloud/PresentationApiClient';

/**
 * DraftDialog — "Entwurf erzeugen": the editor side of POST /api/v1/presentations/draft
 * (contract p-b8b361e72d75, Post #4963).
 *
 * Two modes are exposed here: from a sentence (prompt) and from own media (media).
 * The catalog mode needs an import adapter for structured data and stays API-only for now.
 * The dialog never touches Markdown: the response `content` goes through
 * StoryModel.replaceMarkdown, the current GLOBAL header travels along as `global_header`
 * so a theme chosen before generating survives it.
 */

const RED = '#C1121C';
const CREAM = '#FBF7F4';
const sans = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const serif = "'Iowan Old Style', Palatino, Georgia, serif";

export type DraftAccess =
    | { phase: 'guest'; login: () => void }
    | { phase: 'unverified' }
    | { phase: 'ready' };

export interface DraftDialogProps {
    model: StoryModel;
    access: DraftAccess;
    initialMode?: DraftMode;
    onCreateDraft: (request: DraftRequest) => Promise<DraftResponse>;
    onUploadMedia?: (file: File) => Promise<string>;
    onClose: () => void;
}

const glass: React.CSSProperties = {
    background: 'rgba(20,10,12,0.94)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    border: '1px solid rgba(251,247,244,0.18)',
    color: CREAM,
    fontFamily: sans,
};

const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: 'rgba(251,247,244,0.08)',
    border: '1px solid rgba(251,247,244,0.2)', borderRadius: 9, padding: '10px 12px',
    color: CREAM, fontSize: 14.5, fontFamily: sans,
};

const primaryBtn: React.CSSProperties = {
    background: RED, color: CREAM, border: 0, borderRadius: 999, padding: '11px 20px',
    fontSize: 14.5, fontWeight: 700, cursor: 'pointer', fontFamily: sans,
};

const quietBtn: React.CSSProperties = {
    background: 'transparent', color: CREAM, border: '1px solid rgba(251,247,244,0.3)', borderRadius: 999,
    padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontFamily: sans,
};

/** Storage id out of a media uri the editor already holds (`.../storage/media/123?...`). */
export function storageIdFromUri(uri: string | undefined): number | null {
    if (!uri) return null;
    const m = uri.match(/\/storage\/media\/(\d+)(?:[/?#]|$)/);
    return m ? Number(m[1]) : null;
}

/** Human wording for the contract's error codes; unknown codes fall back to the server message. */
function describeError(error: unknown): string {
    if (error instanceof PresenterApiError) {
        switch (error.code) {
            case 'AUTH_REQUIRED': return 'Bitte melde dich an, um einen Entwurf erzeugen zu lassen.';
            case 'EMAIL_VERIFICATION_REQUIRED': return 'Bitte bestätige zuerst deine E-Mail-Adresse.';
            case 'MEDIA_NOT_FOUND': return 'Mindestens ein Medium gehört nicht zu deinem Konto oder wurde nicht gefunden.';
            case 'INPUT_TOO_LARGE': return 'Die Eingabe ist zu groß. Weniger Medien oder ein kürzerer Text.';
            case 'INVALID_DRAFT_INPUT': return `Eingabe ungültig: ${error.message}`;
            case 'DRAFT_RATE_LIMITED': return 'Dein Kontingent für Entwürfe aus einem Satz ist aufgebraucht.';
            case 'DRAFT_BUDGET_EXHAUSTED': return 'Das Tageskontingent für Entwürfe aus einem Satz ist erschöpft. Bitte morgen erneut versuchen.';
            case 'DRAFT_PROVIDER_UNAVAILABLE': return 'Entwürfe aus einem Satz sind derzeit nicht verfügbar. Aus Medien funktioniert weiterhin.';
            case 'DRAFT_PROVIDER_FAILED': return 'Die Texterzeugung ist fehlgeschlagen. Dein Entwurf bleibt unverändert.';
            default: return error.message;
        }
    }
    return error instanceof Error ? error.message : 'Entwurf konnte nicht erzeugt werden.';
}

export const DraftDialog: React.FC<DraftDialogProps> = ({ model, access, initialMode = 'media', onCreateDraft, onUploadMedia, onClose }) => {
    const [mode, setMode] = useState<DraftMode>(initialMode);
    const [title, setTitle] = useState('');
    const [prompt, setPrompt] = useState('');
    const [maxSteps, setMaxSteps] = useState(8);
    const [mediaIds, setMediaIds] = useState<number[]>(() => {
        const ids = model.steps.map(s => storageIdFromUri(s.uri)).filter((id): id is number => id !== null);
        return [...new Set(ids)].slice(0, 24);
    });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<DraftResponse | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const canSubmit = useMemo(() => {
        if (access.phase !== 'ready' || busy) return false;
        if (mode === 'prompt') return prompt.trim().length > 0 && prompt.length <= 4000;
        if (mode === 'media') return mediaIds.length > 0;
        return false;
    }, [access.phase, busy, mode, prompt, mediaIds]);

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true); setError(null); setResult(null);
        const request: DraftRequest = {
            version: 1,
            mode,
            language: 'de',
            max_steps: maxSteps,
            title: title.trim() ? title.trim().slice(0, 180) : undefined,
            prompt: mode === 'prompt' ? prompt.trim() : undefined,
            media_ids: mode === 'media' ? mediaIds : [],
            global_header: model.globalHeader || undefined,
        };
        try {
            const response = await onCreateDraft(request);
            if (model.dirty && !window.confirm('Der aktuelle Entwurf hat ungespeicherte Änderungen. Durch den erzeugten Entwurf ersetzen?')) {
                setBusy(false);
                return;
            }
            model.replaceMarkdown(response.content);
            setResult(response);
        } catch (reason) {
            setError(describeError(reason));
        } finally {
            setBusy(false);
        }
    };

    const addFiles = async (files: FileList | null) => {
        if (!files || !onUploadMedia) return;
        setUploading(true); setError(null);
        try {
            const next = [...mediaIds];
            for (const file of Array.from(files)) {
                if (next.length >= 24) break;
                const uri = await onUploadMedia(file);
                const id = storageIdFromUri(uri);
                if (id !== null && !next.includes(id)) next.push(id);
            }
            setMediaIds(next);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Upload fehlgeschlagen.');
        } finally {
            setUploading(false);
        }
    };

    const tab = (value: DraftMode, label: string) => (
        <button key={value} onClick={() => setMode(value)} data-testid={`draft-mode-${value}`}
                style={{ ...quietBtn, borderRadius: 9, padding: '8px 13px', fontSize: 13,
                         background: mode === value ? RED : 'transparent', borderColor: mode === value ? RED : 'rgba(251,247,244,0.3)' }}>
            {label}
        </button>
    );

    return (
        <div role="dialog" aria-modal="true" aria-label="Entwurf erzeugen" data-testid="draft-dialog"
             style={{ position: 'absolute', inset: 0, zIndex: 1300, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.45)', padding: 16 }}>
            <div style={{ ...glass, width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', borderRadius: 18, padding: 22 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontFamily: serif, fontSize: 22 }}>Entwurf erzeugen</div>
                    <div onClick={() => !busy && onClose()} style={{ cursor: 'pointer', opacity: 0.6, fontSize: 20, lineHeight: 1 }}>×</div>
                </div>
                <p style={{ fontSize: 13.5, lineHeight: 1.5, opacity: 0.75, margin: '0 0 16px' }}>
                    Aus deinen Medien oder einem Satz entsteht ein bearbeitbarer Entwurf. Dein Thema und Layout bleiben erhalten; gespeichert wird erst, wenn du es willst.
                </p>

                {access.phase === 'guest' && (
                    <div data-testid="draft-gate-guest" style={{ border: '1px solid rgba(251,247,244,0.2)', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 14, lineHeight: 1.5 }}>
                        Zum Erzeugen brauchst du ein bestätigtes Konto. Dein lokaler Entwurf bleibt dabei erhalten.
                        <div style={{ marginTop: 10 }}><button style={primaryBtn} onClick={access.login}>Anmelden oder Konto anlegen</button></div>
                    </div>
                )}
                {access.phase === 'unverified' && (
                    <div data-testid="draft-gate-unverified" style={{ border: '1px solid rgba(251,247,244,0.2)', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 14, lineHeight: 1.5 }}>
                        Bitte bestätige zuerst deine E-Mail-Adresse. Danach kannst du Entwürfe erzeugen lassen.
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {tab('media', 'Aus meinen Medien')}
                    {tab('prompt', 'Aus einem Satz')}
                </div>

                {mode === 'media' && (
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 7, fontWeight: 700 }}>Medien ({mediaIds.length} von 24)</div>
                        {mediaIds.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                                {mediaIds.map(id => (
                                    <span key={id} style={{ fontSize: 12.5, border: '1px solid rgba(251,247,244,0.25)', borderRadius: 999, padding: '4px 10px' }}>
                                        #{id} <span onClick={() => setMediaIds(mediaIds.filter(x => x !== id))} style={{ cursor: 'pointer', opacity: 0.6, marginLeft: 4 }}>×</span>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 10 }}>Noch keine Medien. Lade Bilder, Videos oder 3D-Modelle hoch, jede Datei wird eine Station.</div>
                        )}
                        <div style={{ fontSize: 11.5, lineHeight: 1.4, opacity: 0.7, marginBottom: 8 }}>
                            Hochgeladene Medien sind über ihren Link öffentlich abrufbar. Der Entwurf selbst bleibt privat.
                        </div>
                        {onUploadMedia ? (
                            <label style={{ ...inputStyle, display: 'grid', placeItems: 'center', cursor: uploading ? 'wait' : 'pointer', fontSize: 13, opacity: uploading ? 0.55 : 1 }}>
                                {uploading ? 'Wird hochgeladen …' : 'Dateien hinzufügen'}
                                <input data-testid="draft-upload" type="file" multiple accept="image/*,video/*,.glb,.gltf" disabled={uploading || access.phase !== 'ready'}
                                       style={{ display: 'none' }} onChange={e => { const f = e.target.files; e.target.value = ''; void addFiles(f); }} />
                            </label>
                        ) : (
                            <div style={{ fontSize: 13, opacity: 0.6 }}>Upload ist nach der Anmeldung möglich.</div>
                        )}
                    </div>
                )}

                {mode === 'prompt' && (
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 7, fontWeight: 700 }}>Worum geht es?</div>
                        <textarea data-testid="draft-prompt" value={prompt} maxLength={4000} rows={4} disabled={access.phase !== 'ready'}
                                  placeholder="Eine Produkttour durch unsere drei Helmlinien, für Händler, sachlich." style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
                                  onChange={e => setPrompt(e.target.value)} />
                        <div style={{ fontSize: 12, opacity: 0.55, marginTop: 4 }}>{prompt.length} / 4000 · Texte werden serverseitig erzeugt, keine Bilder.</div>
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10, marginBottom: 16 }}>
                    <div>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 7, fontWeight: 700 }}>Titel (optional)</div>
                        <input data-testid="draft-title" value={title} maxLength={180} style={inputStyle} disabled={access.phase !== 'ready'} onChange={e => setTitle(e.target.value)} />
                    </div>
                    <div>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 7, fontWeight: 700 }}>Stationen</div>
                        <input data-testid="draft-max-steps" type="number" min={4} max={32} value={maxSteps} style={inputStyle} disabled={access.phase !== 'ready'}
                               onChange={e => setMaxSteps(Math.max(4, Math.min(32, parseInt(e.target.value || '8', 10))))} />
                    </div>
                </div>

                {error && <div data-testid="draft-error" style={{ color: '#ff9ca3', fontSize: 13.5, lineHeight: 1.45, marginBottom: 12 }}>{error}</div>}
                {result && (
                    <div data-testid="draft-result" style={{ border: '1px solid rgba(251,247,244,0.2)', borderRadius: 12, padding: 12, marginBottom: 12, fontSize: 13.5, lineHeight: 1.5 }}>
                        Entwurf übernommen: {result.step_count} Stationen{result.selection.omitted_nodes > 0 ? `, ${result.selection.omitted_nodes} Einträge ausgelassen` : ''}.
                        {result.warnings.length > 0 && (
                            <ul style={{ margin: '6px 0 0', paddingLeft: 18, opacity: 0.8 }}>
                                {result.warnings.map(w => <li key={w.code}>{w.message}</li>)}
                            </ul>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button style={quietBtn} onClick={onClose} disabled={busy}>{result ? 'Schließen' : 'Abbrechen'}</button>
                    <button data-testid="draft-submit" style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.45, cursor: canSubmit ? 'pointer' : 'default' }} disabled={!canSubmit} onClick={() => void submit()}>
                        {busy ? 'Wird erzeugt …' : 'Entwurf erzeugen'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DraftDialog;
